import React from 'react';
import { motion } from 'framer-motion';
import { FiInstagram, FiFacebook, FiMail, FiPhone } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Footer = () => {
  const socialLinks = [
    { icon: FiInstagram, href: '#', label: 'Instagram' },
    { icon: FiFacebook, href: '#', label: 'Facebook' },
    { icon: FiMail, href: 'mailto:stay@thalagaha.lk', label: 'Email' },
    { icon: FiPhone, href: 'tel:+94771234567', label: 'Phone' }
  ];

  const quickLinks = [
    { name: 'The Sanctuary', href: '#sanctuary' },
    { name: 'Experiences', href: '#experiences' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Contact', href: '#contact' }
  ];

  const experiences = [
    { name: 'Birdwatching Tours', href: '#' },
    { name: 'Heritage Walks', href: '#' },
    { name: 'Cave Adventures', href: '#' },
    { name: 'Waterfall Circuits', href: '#' }
  ];

  return (
    <footer className="bg-forest-green text-white relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        <motion.div
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            repeatType: 'reverse',
            ease: 'linear'
          }}
          className="w-full h-full opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, white 2px, transparent 2px),
                             radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
            backgroundSize: '100px 100px, 150px 150px'
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
            {/* Brand Column */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h3 className="text-3xl font-playfair font-bold mb-6">
                  Thalagaha Estate
                </h3>
                <p className="text-white/80 font-lato leading-relaxed mb-6 max-w-md">
                  An exclusive nature sanctuary in Kolonna, where the legacy of 
                  Sabaragamuwa chieftains meets the raw beauty of the Sinharaja periphery. 
                  Experience privacy, heritage, and authentic connection with nature.
                </p>
                
                {/* Social Links */}
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.9 }}
                      className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-terracotta-earth/30 transition-colors"
                      aria-label={social.label}
                    >
                      <SafeIcon icon={social.icon} className="w-5 h-5" />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Quick Links */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h4 className="text-xl font-playfair font-semibold mb-6">
                  Explore
                </h4>
                <ul className="space-y-3">
                  {quickLinks.map((link, index) => (
                    <li key={index}>
                      <motion.a
                        href={link.href}
                        whileHover={{ x: 5 }}
                        className="text-white/80 hover:text-white font-lato transition-colors"
                      >
                        {link.name}
                      </motion.a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Experiences */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <h4 className="text-xl font-playfair font-semibold mb-6">
                  Experiences
                </h4>
                <ul className="space-y-3">
                  {experiences.map((experience, index) => (
                    <li key={index}>
                      <motion.a
                        href={experience.href}
                        whileHover={{ x: 5 }}
                        className="text-white/80 hover:text-white font-lato transition-colors"
                      >
                        {experience.name}
                      </motion.a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Contact Info Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="border-t border-white/20 py-8"
        >
          <div className="grid md:grid-cols-3 gap-6 text-center md:text-left">
            <div>
              <h5 className="font-playfair font-semibold mb-2">Location</h5>
              <p className="text-white/80 font-lato text-sm">
                Kolonna, Ratnapura District<br />
                Sabaragamuwa Province, Sri Lanka
              </p>
            </div>
            <div>
              <h5 className="font-playfair font-semibold mb-2">Reservations</h5>
              <p className="text-white/80 font-lato text-sm">
                +94 77 123 4567<br />
                stay@thalagaha.lk
              </p>
            </div>
            <div>
              <h5 className="font-playfair font-semibold mb-2">Best Rate</h5>
              <p className="text-white/80 font-lato text-sm">
                Book direct and save 10%<br />
                Free cancellation up to 48hrs
              </p>
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
              className="text-white/60 font-lato text-sm mb-4 md:mb-0"
            >
              © 2025 Thalagaha Estate. All rights reserved. | Privacy Policy | Terms of Service
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
              viewport={{ once: true }}
              className="text-white/60 font-lato text-sm"
            >
              Designed and Created by Rajantha
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;